// src/pages/Locations.tsx - FastMag Location Management (Fixed)
import { supabase } from '@/lib/supabase';
import type { Article, Location } from '@/types';
import {
  logArticleAssigned,
  logArticleMoved,
  logArticleUnassigned,
  logLocationCreated,
  logLocationDeleted,
  logLocationUpdated
} from '@/utils/activityLogger';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronRight,
  Edit2,
  MapPin,
  Move,
  Package,
  Plus,
  Search,
  Trash2
} from 'lucide-react';
import { useMemo, useState } from 'react';
import Select from '../components/common/Select';

interface LocationNode extends Location {
  children: LocationNode[];
  full_path?: string;
}

interface LocationWithPath extends Location {
  full_path: string;
}


export default function Locations() {
  const queryClient = useQueryClient();
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [expandedArticles, setExpandedArticles] = useState<Set<string>>(new Set());
  const [selectedLocation, setSelectedLocation] = useState<LocationNode | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);

  // Form states
  const [formDescription, setFormDescription] = useState('');
  const [selectedArticles, setSelectedArticles] = useState<string[]>([]);
  const [selectedArticleToMove, setSelectedArticleToMove] = useState<Article | null>(null);
  const [targetLocationId, setTargetLocationId] = useState('');

  // Fetch locations
  const { data: locations = [], isLoading: loadingLocations } = useQuery<Location[]>({
    queryKey: ['locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .order('sort_order');

      if (error) throw error;
      return data;
    }
  });

  // Fetch articles
  const { data: articles = [], isLoading: loadingArticles } = useQuery<Article[]>({
    queryKey: ['articles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .order('codice_prodotto');

      if (error) {
        console.warn('Articles table not found');
        return [];
      }
      return data;
    },
    retry: false
  });

  // Build full path from parent_id (SINGLE SOURCE OF TRUTH)
  const getFullPath = (locationId: string, locationsArray: Location[] = locations): string => {
    const location = locationsArray.find(l => l.id === locationId);
    if (!location) return '';

    const path: string[] = [];
    let current: Location | undefined | null = location;

    while (current) {
      path.unshift(current.description);
      current = current.parent_id ? locationsArray.find(l => l.id === current?.parent_id) : null;
    }

    return path.join(' > ');
  };

  // Create location map with computed paths (memoized for performance)
  const locationsWithPaths = useMemo<LocationWithPath[]>(() => {
    return locations.map(loc => ({
      ...loc,
      full_path: getFullPath(loc.id, locations)
    }));
  }, [locations]);

  // Build tree structure
  const buildTree = (items: LocationWithPath[], parentId: string | null = null): LocationNode[] => {
    return items
      .filter(item => item.parent_id === parentId)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map(item => ({
        ...item,
        children: buildTree(items, item.id)
      }));
  };

  // Get filtered tree
  const getFilteredTree = (): LocationNode[] => {
    if (!searchQuery) return buildTree(locationsWithPaths);

    const query = searchQuery.toLowerCase();

    // Find locations matching query
    const matchingLocationIds = new Set<string>();

    locationsWithPaths.forEach(loc => {
      if (
        loc.description.toLowerCase().includes(query) ||
        loc.full_path.toLowerCase().includes(query)
      ) {
        matchingLocationIds.add(loc.id);
      }
    });

    // Find locations with matching articles
    const articlesMatchingQuery = articles.filter(art =>
      art.codice_prodotto?.toLowerCase().includes(query) ||
      art.descrizione_prodotto?.toLowerCase().includes(query)
    );

    articlesMatchingQuery.forEach(art => {
      if (art.posizione_magazzino_id) {
        matchingLocationIds.add(art.posizione_magazzino_id);
      }
    });

    // Include parent locations
    const locationIdsWithParents = new Set(matchingLocationIds);
    matchingLocationIds.forEach(id => {
      let current = locationsWithPaths.find(l => l.id === id);
      while (current?.parent_id) {
        locationIdsWithParents.add(current.parent_id);
        current = locationsWithPaths.find(l => l.id === current?.parent_id);
      }
    });

    const filtered = locationsWithPaths.filter(loc => locationIdsWithParents.has(loc.id));
    return buildTree(filtered);
  };

  const tree = getFilteredTree();

  // Get articles for location
  const getArticlesForLocation = (locationId: string): Article[] => {
    return articles.filter(art => art.posizione_magazzino_id === locationId);
  };

  // Get customer_uuid helper
  const getCustomerUuid = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: profile } = await supabase
      .from('profiles')
      .select('customer_uuid')
      .eq('id', user.id)
      .single();

    if (!profile?.customer_uuid) throw new Error('Customer UUID not found');
    return profile.customer_uuid;
  };

  // Create location
  const createLocation = useMutation<any, Error, { description: string; parentId: string | null }>({
    mutationFn: async ({ description, parentId }) => {
      const customerUuid = await getCustomerUuid();

      // Get max sort_order for same parent
      let query = supabase
        .from('locations')
        .select('sort_order')
        .order('sort_order', { ascending: false })
        .limit(1);

      if (parentId) {
        query = query.eq('parent_id', parentId);
      } else {
        query = query.is('parent_id', null);
      }

      const { data: siblings } = await query;

      const sortOrder = siblings?.[0]?.sort_order ? siblings[0].sort_order + 1 : 0;

      const { data, error } = await supabase
        .from('locations')
        .insert([{
          description,
          parent_id: parentId,
          customer_uuid: customerUuid,
          sort_order: sortOrder
        }])
        .select()
        .single();

      if (error) throw error;

      // Compute parent path
      const parentPath = parentId ? getFullPath(parentId, locations) : null;

      return { ...data, parentPath };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setShowAddModal(false);
      setFormDescription('');
      // Log activity
      if (data) {
        logLocationCreated(data.id, data.description, data.parentPath);
      }
    },
    onError: (error) => {
      alert(`Errore: ${error.message}`);
    }
  });

  // Update location
  const updateLocation = useMutation<any, Error, { id: string; description: string }>({
    mutationFn: async ({ id, description }) => {
      const { error } = await supabase
        .from('locations')
        .update({ description })
        .eq('id', id);

      if (error) throw error;
      return { id, description };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setShowEditModal(false);
      // Log activity
      if (data) {
        logLocationUpdated(data.id, data.description);
      }
    },
    onError: (error) => {
      alert(`Errore: ${error.message}`);
    }
  });

  // Delete location
  const deleteLocation = useMutation<any, Error, { id: string; description: string }>({
    mutationFn: async ({ id, description }) => {
      // Check for articles
      const articlesInLocation = getArticlesForLocation(id);
      if (articlesInLocation.length > 0) {
        throw new Error('Impossibile eliminare: la posizione contiene articoli');
      }

      // Check for children
      const hasChildren = locations.some(l => l.parent_id === id);
      if (hasChildren) {
        throw new Error('Impossibile eliminare: la posizione contiene sotto-posizioni');
      }

      const { error } = await supabase
        .from('locations')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { id, description };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setShowDeleteConfirm(false);
      // Log activity
      if (data) {
        logLocationDeleted(data.id, data.description);
      }
    },
    onError: (error) => {
      alert(error.message);
    }
  });

  // Assign articles to location
  const assignArticles = useMutation<any, Error, { articleIds: string[]; locationId: string }>({
    mutationFn: async ({ articleIds, locationId }) => {
      // Get article details and location path for logging
      const assignedArticles = articles.filter(a => articleIds.includes(a.id));
      const locationPath = getFullPath(locationId, locations);

      const { error } = await supabase
        .from('articles')
        .update({ posizione_magazzino_id: locationId })
        .in('id', articleIds);

      if (error) throw error;
      return { assignedArticles, locationPath };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      setShowAssignModal(false);
      setSelectedArticles([]);
      // Log activity for each assigned article
      if (data?.assignedArticles && data?.locationPath) {
        data.assignedArticles.forEach((article: Article) => {
          logArticleAssigned(article.id, article.codice_prodotto, data.locationPath);
        });
      }
    },
    onError: (error) => {
      alert(`Errore: ${error.message}`);
    }
  });

  // Move article to different location
  const moveArticle = useMutation<any, Error, { articleId: string; targetLocationId: string; fromLocationId?: string }>({
    mutationFn: async ({ articleId, targetLocationId, fromLocationId }) => {
      // Get article and location details for logging
      const article = articles.find(a => a.id === articleId);
      const fromPath = fromLocationId ? getFullPath(fromLocationId, locations) : 'Sconosciuto';
      const toPath = getFullPath(targetLocationId, locations);

      const { error } = await supabase
        .from('articles')
        .update({ posizione_magazzino_id: targetLocationId })
        .eq('id', articleId);

      if (error) throw error;
      return {
        articleCode: article?.codice_prodotto,
        fromPath,
        toPath,
        articleId
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      setShowMoveModal(false);
      setSelectedArticleToMove(null);
      setTargetLocationId('');
      // Log activity
      if (data && data.articleCode && data.fromPath && data.toPath) {
        logArticleMoved(data.articleId, data.articleCode, data.fromPath, data.toPath);
      }
    },
    onError: (error) => {
      alert(`Errore: ${error.message}`);
    }
  });

  // Remove article from location
  const removeArticle = useMutation<any, Error, { articleId: string; articleCode: string; locationId: string }>({
    mutationFn: async ({ articleId, articleCode, locationId }) => {
      const locationPath = getFullPath(locationId, locations);

      const { error } = await supabase
        .from('articles')
        .update({ posizione_magazzino_id: null })
        .eq('id', articleId);

      if (error) throw error;
      return { articleId, articleCode, locationPath };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      // Log activity
      if (data) {
        logArticleUnassigned(data.articleId, data.articleCode, data.locationPath);
      }
    },
    onError: (error) => {
      alert(`Errore: ${error.message}`);
    }
  });

  // Toggle functions
  const toggleNode = (nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const toggleArticleList = (nodeId: string) => {
    setExpandedArticles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const toggleArticleSelection = (articleId: string) => {
    setSelectedArticles(prev =>
      prev.includes(articleId)
        ? prev.filter(id => id !== articleId)
        : [...prev, articleId]
    );
  };

  // Get unassigned articles
  const getUnassignedArticles = () => {
    return articles.filter(art => !art.posizione_magazzino_id);
  };

  // Get leaf location options (for dropdowns)
  const getLeafLocations = () => {
    return locationsWithPaths.filter(loc => {
      const parentIdMatches = locations.some(l => l.parent_id === loc.id);
      return !parentIdMatches && loc.id !== selectedLocation?.id;
    });
  };

  // Highlight search text
  const highlightText = (text: string, query: string) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? <mark key={i} className="bg-yellow-200">{part}</mark> : part
    );
  };

  // Render tree node
  const renderTreeNode = (node: LocationNode, level: number = 0) => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children?.length > 0;
    const nodeArticles = getArticlesForLocation(node.id);
    const isArticlesExpanded = expandedArticles.has(node.id);
    const isLeaf = !hasChildren;

    return (
      <div key={node.id} className="border-b border-slate-200">
        <div
          className={`flex items-center justify-between p-3 hover:bg-slate-50 transition-colors ${selectedLocation?.id === node.id ? 'bg-blue-50' : ''
            }`}
          style={{ paddingLeft: `${level * 1.5 + 0.75}rem` }}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {hasChildren ? (
              <button
                onClick={() => toggleNode(node.id)}
                className="p-1 hover:bg-slate-200 rounded transition-colors flex-shrink-0"
              >
                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            ) : (
              <span className="w-6"></span>
            )}

            <MapPin className="w-4 h-4 text-slate-500 flex-shrink-0" />

            <span className="font-medium text-slate-800 truncate">
              {searchQuery ? highlightText(node.description, searchQuery) : node.description}
            </span>

            {nodeArticles.length > 0 && (
              <button
                onClick={() => toggleArticleList(node.id)}
                className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold hover:bg-blue-200 transition-colors flex-shrink-0"
              >
                <Package className="w-3 h-3" />
                <span>{nodeArticles.length}</span>
                {isArticlesExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              </button>
            )}
          </div>

          <div className="flex gap-1 flex-shrink-0">
            {isLeaf && (
              <>
                <button
                  onClick={() => {
                    setSelectedLocation(node);
                    setShowAssignModal(true);
                  }}
                  className="p-2 hover:bg-blue-100 text-blue-600 rounded transition-colors touch-manipulation"
                  title="Assegna articoli"
                >
                  <Package className="w-4 h-4" />
                </button>
                {nodeArticles.length > 0 && (
                  <button
                    onClick={() => {
                      setSelectedLocation(node);
                      setShowMoveModal(true);
                    }}
                    className="p-2 hover:bg-purple-100 text-purple-600 rounded transition-colors touch-manipulation"
                    title="Sposta articoli"
                  >
                    <Move className="w-4 h-4" />
                  </button>
                )}
              </>
            )}
            <button
              onClick={() => {
                setSelectedLocation(node);
                setFormDescription('');
                setShowAddModal(true);
              }}
              className="p-2 hover:bg-green-100 text-green-600 rounded transition-colors touch-manipulation"
              title="Aggiungi sotto-posizione"
            >
              <Plus className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                setSelectedLocation(node);
                setFormDescription(node.description);
                setShowEditModal(true);
              }}
              className="p-2 hover:bg-blue-100 text-blue-600 rounded transition-colors touch-manipulation"
              title="Modifica"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            {nodeArticles.length === 0 && !hasChildren && (
              <button
                onClick={() => {
                  setSelectedLocation(node);
                  setShowDeleteConfirm(true);
                }}
                className="p-2 hover:bg-red-100 text-red-600 rounded transition-colors touch-manipulation"
                title="Elimina"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Articles list */}
        {isArticlesExpanded && nodeArticles.length > 0 && (
          <div className="bg-slate-50 border-t border-slate-200" style={{ paddingLeft: `${(level + 1) * 1.5 + 0.75}rem` }}>
            {nodeArticles.map(article => (
              <div key={article.id} className="flex items-center justify-between p-2 border-b border-slate-200">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Package className="w-3 h-3 text-slate-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {article.codice_prodotto}
                    </p>
                    <p className="text-xs text-slate-600 truncate">
                      {article.descrizione_prodotto}
                    </p>
                  </div>
                  <span className="text-xs text-slate-500 flex-shrink-0">
                    Qty: {article.quantita_magazzino_attuale}
                  </span>
                </div>
                <button
                  onClick={() => removeArticle.mutate({
                    articleId: article.id,
                    articleCode: article.codice_prodotto,
                    locationId: node.id
                  })}
                  className="p-1 hover:bg-red-100 text-red-600 rounded transition-colors touch-manipulation flex-shrink-0"
                  title="Rimuovi"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Children */}
        {isExpanded && hasChildren && (
          <div>
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loadingLocations || loadingArticles) {
    return (
      <div className="p-4 md:p-8 min-h-screen bg-slate-50">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-800 mb-2">Posizioni Magazzino</h1>
              <p className="text-slate-600">Gestisci la struttura delle posizioni</p>
            </div>
            <button
              onClick={() => {
                setSelectedLocation(null);
                setFormDescription('');
                setShowAddModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 touch-manipulation"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Aggiungi Posizione</span>
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cerca posizioni o articoli..."
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Tree */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {tree.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                {searchQuery ? 'Nessuna posizione trovata' : 'Nessuna posizione'}
              </h3>
              {!searchQuery && (
                <button
                  onClick={() => {
                    setSelectedLocation(null);
                    setFormDescription('');
                    setShowAddModal(true);
                  }}
                  className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors inline-flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Aggiungi Prima Posizione
                </button>
              )}
            </div>
          ) : (
            <div>
              {tree.map(node => renderTreeNode(node))}
            </div>
          )}
        </div>

        {/* Add Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">
                {selectedLocation ? 'Aggiungi Sotto-Posizione' : 'Aggiungi Posizione'}
              </h3>
              {selectedLocation && (
                <div className="mb-4 p-3 bg-blue-50 rounded text-sm">
                  <strong>Posizione padre:</strong> {selectedLocation.full_path}
                </div>
              )}
              <input
                type="text"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Nome posizione..."
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                autoFocus
              />
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors"
                >
                  Annulla
                </button>
                <button
                  onClick={() => {
                    if (formDescription.trim()) {
                      createLocation.mutate({
                        description: formDescription.trim(),
                        parentId: selectedLocation?.id || null
                      });
                    }
                  }}
                  disabled={!formDescription.trim() || createLocation.isPending}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg transition-colors"
                >
                  {createLocation.isPending ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Modifica Posizione</h3>
              <div className="mb-4 p-3 bg-blue-50 rounded text-sm">
                <strong>Percorso attuale:</strong> {selectedLocation?.full_path}
              </div>
              <input
                type="text"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Nome posizione..."
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                autoFocus
              />
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors"
                >
                  Annulla
                </button>
                <button
                  onClick={() => {
                    if (formDescription.trim() && selectedLocation) {
                      updateLocation.mutate({
                        id: selectedLocation.id,
                        description: formDescription.trim()
                      });
                    }
                  }}
                  disabled={!formDescription.trim() || updateLocation.isPending}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg transition-colors"
                >
                  {updateLocation.isPending ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Conferma Eliminazione</h3>
              <p className="text-slate-600 mb-6">
                Sei sicuro di voler eliminare la posizione <span className="font-semibold">"{selectedLocation?.description}"</span>?
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors"
                >
                  Annulla
                </button>
                <button
                  onClick={() => {
                    if (selectedLocation) {
                      deleteLocation.mutate({
                        id: selectedLocation.id,
                        description: selectedLocation.description
                      });
                    }
                  }}
                  disabled={deleteLocation.isPending}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  {deleteLocation.isPending ? 'Eliminazione...' : 'Elimina'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Assign Articles Modal */}
        {showAssignModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-800">Assegna Articoli</h3>
                <div className="mt-2 p-3 bg-blue-50 rounded text-sm">
                  <strong>Posizione:</strong> {selectedLocation?.full_path}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                {getUnassignedArticles().length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Nessun articolo non assegnato</p>
                ) : (
                  <div className="space-y-2">
                    {getUnassignedArticles().map(article => (
                      <label key={article.id} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedArticles.includes(article.id)}
                          onChange={() => toggleArticleSelection(article.id)}
                          className="w-4 h-4"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">{article.codice_prodotto}</p>
                          <p className="text-sm text-slate-600 truncate">{article.descrizione_prodotto}</p>
                        </div>
                        <span className="text-sm text-slate-500 flex-shrink-0">Qty: {article.quantita_magazzino_attuale}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-6 border-t border-slate-200 flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowAssignModal(false);
                    setSelectedArticles([]);
                  }}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors"
                >
                  Annulla
                </button>
                <button
                  onClick={() => {
                    if (selectedArticles.length > 0) {
                      if (selectedLocation) {
                        assignArticles.mutate({
                          articleIds: selectedArticles,
                          locationId: selectedLocation.id
                        });
                      }
                    }
                  }}
                  disabled={selectedArticles.length === 0 || assignArticles.isPending}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg transition-colors"
                >
                  {assignArticles.isPending ? 'Assegnazione...' : `Assegna (${selectedArticles.length})`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Move Article Modal */}
        {showMoveModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-800">Sposta Articoli</h3>
                <div className="mt-2 p-3 bg-blue-50 rounded text-sm">
                  <strong>Da:</strong> {selectedLocation?.full_path}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {/* Select article */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Seleziona Articolo</label>
                  <div className="space-y-2 max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2">
                    {getArticlesForLocation(selectedLocation?.id || '').map(article => (
                      <label key={article.id} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded cursor-pointer">
                        <input
                          type="radio"
                          name="articleToMove"
                          checked={selectedArticleToMove?.id === article.id}
                          onChange={() => setSelectedArticleToMove(article)}
                          className="w-4 h-4"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 text-sm truncate">{article.codice_prodotto}</p>
                          <p className="text-xs text-slate-600 truncate">{article.descrizione_prodotto}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Select destination */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Posizione Destinazione</label>
                  <Select
                    value={targetLocationId}
                    onChange={(e) => setTargetLocationId(e.target.value)}
                    options={[
                      { value: '', label: 'Seleziona posizione...' },
                      ...getLeafLocations().map(loc => ({ value: loc.id, label: loc.full_path }))
                    ]}
                    placeholder="Seleziona posizione..."
                    emptyMessage="Nessuna posizione disponibile"
                    name="targetLocationId"
                  />
                </div>
              </div>
              <div className="p-6 border-t border-slate-200 flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowMoveModal(false);
                    setSelectedArticleToMove(null);
                    setTargetLocationId('');
                  }}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors"
                >
                  Annulla
                </button>
                <button
                  onClick={() => {
                    if (selectedArticleToMove && targetLocationId) {
                      moveArticle.mutate({
                        articleId: selectedArticleToMove.id,
                        targetLocationId,
                        fromLocationId: selectedLocation?.id
                      });
                    }
                  }}
                  disabled={!selectedArticleToMove || !targetLocationId || moveArticle.isPending}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white rounded-lg transition-colors"
                >
                  {moveArticle.isPending ? 'Spostamento...' : 'Sposta'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}